import React, { useImperativeHandle, useRef, useState } from 'react';
import { buildContactMention, displaySuccessInvite } from './helper';
import { ContactSchema } from '../../definitions/schemas';
import { DRAWER_Z_INDEX } from '../../definitions/constants';
import { EventProperty } from '../../tools/Analytics';
import { InsertMentiontype } from '../Input/RichTextEditor';
import { OrganizationFlagEnum } from '../../lib/features/FeatureFlag';
import { stopEffects } from '../../lib/Event';
import ShareRoomDialog, {
  ShareRoomDialogProps,
} from '../Rooms/Share/ShareRoomDialog';
import useFeatureFlag from '../../lib/hook/useFeatureFlag';

export interface WithOwnerInvitationProps
  extends Pick<
    ShareRoomDialogProps,
    | 'actionLabel'
    | 'contactId'
    | 'forceEmailInput'
    | 'onContactsInvited'
    | 'roomId'
    | 'title'
  > {
  handleContactsInvited: ShareRoomDialogProps['onContactsInvited'];
  trackingProperties?: EventProperty;
}

// Define the methods and properties you want to expose
export interface MentionInviteMethods {
  onOwnerInvite: (email: string, onInsertMention: InsertMentiontype) => void;
}

const MentionInvite: React.ForwardRefRenderFunction<
  MentionInviteMethods,
  WithOwnerInvitationProps
> = (props, ref) => {
  const {
    handleContactsInvited,
    trackingProperties = {},
    ...ShareRoomDialogProps
  } = props;

  const areSmsNotificationsEnabled = useFeatureFlag(
    OrganizationFlagEnum.SMS_NOTIFICATIONS
  );

  const [invitationDialogOpen, setInvitationDialogOpen] = useState(false);
  const [emails, setEmails] = useState<Array<string>>();
  const [mobileFullNumbers, setMobileFullNumbers] = useState<string[]>();
  const [mobilePhones, setMobilePhones] = useState<string[]>();

  const insertMentionFunction = useRef<InsertMentiontype>();

  useImperativeHandle(
    ref,
    () => ({
      onOwnerInvite,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const handleClose = () => {
    setInvitationDialogOpen(false);
    setEmails(undefined);
    setMobileFullNumbers(undefined);
    setMobilePhones(undefined);
  };

  const assignTerm = (term: string) => {
    if (areSmsNotificationsEnabled) {
      setMobileFullNumbers([term]);
      setMobilePhones([term]);
      return;
    }
    setEmails([term]);
  };

  const onOwnerInvite = (term: string, onInsertMention: InsertMentiontype) => {
    insertMentionFunction.current = onInsertMention;
    assignTerm(term);
    setInvitationDialogOpen(true);
  };

  const onContactsInvited = (invitedContacts: ContactSchema[] = []) => {
    handleContactsInvited?.(invitedContacts);
    if (invitedContacts?.length && insertMentionFunction.current) {
      insertMentionFunction.current(buildContactMention(invitedContacts[0]));
    }
    displaySuccessInvite(invitedContacts?.[0]);
  };

  return (
    <ShareRoomDialog
      {...ShareRoomDialogProps}
      DialogProps={{
        disableEscapeKeyDown: true,
        onClick: stopEffects(),
        style: { zIndex: DRAWER_Z_INDEX + 1 },
      }}
      disableAddMore
      disableDescription
      disableSuccessToast
      emails={emails}
      mobileFullNumbers={mobileFullNumbers}
      mobilePhones={mobilePhones}
      onClose={handleClose}
      onContactsInvited={onContactsInvited}
      open={invitationDialogOpen}
      trackingProperties={trackingProperties}
    />
  );
};

export default MentionInvite;
